import React, { useEffect, useState } from "react";
import { Sparkles, Loader2, Layers, Film, Image as ImageIcon, Mail, MessageSquare, CheckCircle2, Calendar, ArrowRight, Wand2, RefreshCcw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/Page";

const tones = ["Engaging", "Warm", "Urgent", "Playful", "Professional"];
const platforms = ["instagram", "facebook", "tiktok", "youtube-shorts", "linkedin"];
const aspects = ["1:1", "4:5", "9:16", "16:9"];

const channelMeta = [
  { key: "video", icon: Film, label: "Video", color: "bg-purple-100 text-purple-700" },
  { key: "social", icon: ImageIcon, label: "Social Post", color: "bg-pink-100 text-pink-700" },
  { key: "email", icon: Mail, label: "Email", color: "bg-blue-100 text-blue-700" },
  { key: "sms", icon: MessageSquare, label: "SMS", color: "bg-emerald-100 text-emerald-700" },
];

const CampaignBundle = () => {
  const backendBase = process.env.REACT_APP_BACKEND_URL;

  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("Engaging");
  const [platform, setPlatform] = useState("instagram");
  const [aspect, setAspect] = useState("1:1");
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));

  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState({});
  const [bundle, setBundle] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedCount, setSavedCount] = useState(null);

  const [past, setPast] = useState([]);
  const [regenImg, setRegenImg] = useState(false);

  useEffect(() => {
    api.get("/campaign-bundle/list").then(({ data }) => setPast(data.bundles || [])).catch(() => {});
  }, [bundle]);

  const generate = async () => {
    if (!topic.trim()) return toast.error("Add a topic first");
    setGenerating(true); setBundle(null); setSavedCount(null);
    setProgress({ video: "running", social: "running", email: "running", sms: "running" });
    try {
      const { data } = await api.post("/campaign-bundle/generate", {
        topic, tone: tone.toLowerCase(), platform, aspect_ratio: aspect,
      });
      setBundle(data);
      setProgress({
        video: data.video_idea ? "done" : "failed",
        social: data.social_caption && data.image ? "done" : data.social_caption ? "partial" : "failed",
        email: data.email ? "done" : "failed",
        sms: data.sms ? "done" : "failed",
      });
      toast.success("Bundle generated");
    } catch (e) {
      toast.error("Generation failed");
      setProgress({});
    } finally {
      setGenerating(false);
    }
  };

  const saveAll = async () => {
    if (!bundle) return;
    setSaving(true);
    try {
      const { data } = await api.post("/campaign-bundle/save", {
        bundle_id: bundle.id,
        start_date: startDate ? new Date(startDate).toISOString() : null,
      });
      setSavedCount(data.count);
      toast.success(`${data.count} items added to your Calendar`);
    } catch {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const reset = () => { setBundle(null); setProgress({}); setSavedCount(null); };

  const regenerateImage = async () => {
    if (!bundle) return;
    setRegenImg(true);
    try {
      const { data } = await api.post(`/campaign-bundle/${bundle.id}/regenerate-image`);
      setBundle({ ...bundle, image: data });
      setProgress((p) => ({ ...p, social: bundle.social_caption ? "done" : "partial" }));
      toast.success("Image regenerated");
    } catch {
      toast.error("Image regen failed");
    } finally { setRegenImg(false); }
  };

  const formatCaption = (sc) => {
    if (!sc) return "";
    return `${sc.hook || ""}\n\n${sc.body || ""}\n\n${sc.cta || ""}\n\n${(sc.hashtags || []).join(" ")}`.trim();
  };

  return (
    <PageContainer>
      <PageHeader
        title={<span className="flex items-center gap-2"><Layers className="text-[#7C3AED]" size={26} /> Campaign Bundle</span>}
        subtitle="One topic → matching video, social post, email, and SMS in 60 seconds."
        testId="bundle-header"
      />

      {!bundle && !generating && (
        <div className="max-w-2xl mx-auto" data-testid="bundle-input">
          <div className="op-card p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">What's the campaign about?</h2>
            <p className="text-sm text-gray-500 mb-4">Optimus Prime will fan it out across all 4 channels in parallel.</p>
            <textarea
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g. Mother's Day brunch with bottomless mimosas"
              rows={3}
              className="op-input mb-4"
              data-testid="bundle-topic"
            />

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs font-semibold text-gray-700">Tone</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {tones.map((t) => (
                    <button key={t} onClick={() => setTone(t)} className={tone === t ? "op-pill-active" : "op-pill-inactive"} data-testid={`bundle-tone-${t.toLowerCase()}`}>{t}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700">Social platform</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {platforms.map((p) => (
                    <button key={p} onClick={() => setPlatform(p)} className={platform === p ? "op-pill-active" : "op-pill-inactive"} data-testid={`bundle-platform-${p}`}>{p.replace("-shorts", "")}</button>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-5">
              <div>
                <label className="text-xs font-semibold text-gray-700">Image aspect</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {aspects.map((a) => (
                    <button key={a} onClick={() => setAspect(a)} className={aspect === a ? "op-pill-active" : "op-pill-inactive"} data-testid={`bundle-aspect-${a.replace(":","x")}`}>{a}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700">Schedule start date</label>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="op-input mt-1" data-testid="bundle-start-date" />
                <p className="text-[11px] text-gray-400 mt-1">Items spread Day 0–3 starting here.</p>
              </div>
            </div>

            <button onClick={generate} disabled={!topic.trim()} className="op-btn-primary w-full flex items-center justify-center gap-2" data-testid="bundle-generate">
              <Wand2 size={16} /> Generate full bundle (~20s)
            </button>
          </div>

          {past.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2 px-1">Recent bundles</h3>
              <div className="space-y-2">
                {past.slice(0, 5).map((b) => (
                  <div key={b.id} className="op-card p-3 flex items-center justify-between" data-testid={`bundle-past-${b.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{b.topic}</p>
                      <p className="text-xs text-gray-400">{new Date(b.created_at).toLocaleString()} · {b.status}</p>
                    </div>
                    <span className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full ${b.status === "saved" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>{b.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {generating && (
        <div className="max-w-xl mx-auto op-card p-8 text-center" data-testid="bundle-loading">
          <Sparkles className="mx-auto text-[#7C3AED] mb-3 animate-pulse" size={28} />
          <h3 className="text-lg font-semibold mb-1">Optimus Prime is fanning it out…</h3>
          <p className="text-sm text-gray-500 mb-6">All 4 channels are being generated in parallel.</p>
          <div className="space-y-2">
            {channelMeta.map((c) => {
              const state = progress[c.key];
              const Icon = c.icon;
              return (
                <div key={c.key} className="flex items-center gap-3 px-4 py-2 bg-gray-50 rounded-lg">
                  <Icon size={16} className="text-gray-500" />
                  <span className="text-sm text-gray-700 flex-1 text-left">{c.label}</span>
                  {state === "running" && <Loader2 size={14} className="animate-spin text-[#7C3AED]" />}
                  {state === "done" && <CheckCircle2 size={14} className="text-emerald-500" />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {bundle && !generating && (
        <div className="max-w-6xl mx-auto" data-testid="bundle-result">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Topic</p>
              <h2 className="text-xl font-bold text-gray-900">{bundle.topic}</h2>
            </div>
            <button onClick={reset} className="op-btn-secondary" data-testid="bundle-new">New bundle</button>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {/* Video card */}
            <div className="op-card p-5" data-testid="bundle-video-card">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center"><Film size={16} /></div>
                <h3 className="font-semibold text-gray-900">Video draft</h3>
                {progress.video === "done" && <CheckCircle2 size={14} className="text-emerald-500 ml-auto" />}
              </div>
              {bundle.video_idea ? (
                <div className="text-sm space-y-2">
                  <p className="font-semibold text-gray-900">{bundle.video_idea.title}</p>
                  <p className="text-gray-600">{bundle.video_idea.hook}</p>
                  <ol className="text-xs text-gray-500 pl-4 space-y-0.5 list-decimal">
                    {(bundle.video_idea.beats || []).map((b, i) => <li key={i}>{b}</li>)}
                  </ol>
                  <p className="text-xs text-[#7C3AED] font-medium pt-1">CTA: {bundle.video_idea.cta}</p>
                </div>
              ) : <p className="text-sm text-gray-400">Generation skipped.</p>}
            </div>

            {/* Social card */}
            <div className="op-card p-5" data-testid="bundle-social-card">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-pink-100 text-pink-700 flex items-center justify-center"><ImageIcon size={16} /></div>
                <h3 className="font-semibold text-gray-900">Social Post · {bundle.platform}</h3>
                {progress.social === "done" && <CheckCircle2 size={14} className="text-emerald-500 ml-auto" />}
              </div>
              {bundle.image?.image_url && (
                <img src={`${backendBase}${bundle.image.image_url}`} alt="post" className="w-full rounded-lg mb-3 max-h-64 object-cover" data-testid="bundle-image" />
              )}
              {!bundle.image?.image_url && (
                <div className="w-full rounded-lg mb-3 p-4 bg-amber-50 border border-amber-200 flex items-start gap-3" data-testid="bundle-image-missing">
                  <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-amber-900">Image generation failed</p>
                    <p className="text-[11px] text-amber-700 mt-0.5">Nano Banana hiccupped. One retry usually works.</p>
                  </div>
                  <button onClick={regenerateImage} disabled={regenImg} className="op-btn-secondary text-xs flex items-center gap-1 flex-shrink-0" data-testid="bundle-regen-image">
                    {regenImg ? <Loader2 size={12} className="animate-spin" /> : <RefreshCcw size={12} />} Retry
                  </button>
                </div>
              )}
              {bundle.social_caption && (
                <div className="text-sm text-gray-700 whitespace-pre-line max-h-40 overflow-y-auto">
                  {formatCaption(bundle.social_caption)}
                </div>
              )}
            </div>

            {/* Email card */}
            <div className="op-card p-5" data-testid="bundle-email-card">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center"><Mail size={16} /></div>
                <h3 className="font-semibold text-gray-900">Email</h3>
                {progress.email === "done" && <CheckCircle2 size={14} className="text-emerald-500 ml-auto" />}
              </div>
              {bundle.email ? (
                <div className="text-sm space-y-2">
                  <p className="font-semibold">{bundle.email.subject}</p>
                  {bundle.email.preview && <p className="text-xs text-gray-400 italic">{bundle.email.preview}</p>}
                  <p className="text-gray-700 whitespace-pre-line text-xs leading-relaxed max-h-40 overflow-y-auto">{bundle.email.body}</p>
                </div>
              ) : <p className="text-sm text-gray-400">Generation skipped.</p>}
            </div>

            {/* SMS card */}
            <div className="op-card p-5" data-testid="bundle-sms-card">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center"><MessageSquare size={16} /></div>
                <h3 className="font-semibold text-gray-900">SMS</h3>
                {progress.sms === "done" && <CheckCircle2 size={14} className="text-emerald-500 ml-auto" />}
              </div>
              {bundle.sms ? (
                <div>
                  <p className="text-sm text-gray-800 bg-gray-50 p-3 rounded-lg leading-relaxed">{bundle.sms.message}</p>
                  <p className="text-xs text-gray-400 mt-2">{(bundle.sms.message || "").length} / 160 chars</p>
                </div>
              ) : <p className="text-sm text-gray-400">Generation skipped.</p>}
            </div>
          </div>

          {/* Save bar */}
          <div className="op-card p-5 sticky bottom-4 backdrop-blur bg-white/95">
            {savedCount === null ? (
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <Calendar className="text-[#7C3AED]" size={18} />
                  <div>
                    <p className="text-sm font-semibold">Schedule across 4 days</p>
                    <p className="text-xs text-gray-500">Day 0 video · Day 1 social · Day 2 email · Day 3 SMS</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="op-input text-sm" data-testid="bundle-save-date" />
                  <button onClick={saveAll} disabled={saving} className="op-btn-primary flex items-center gap-2" data-testid="bundle-save-all">
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} Save all to Calendar
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3" data-testid="bundle-saved">
                <CheckCircle2 className="text-emerald-500" size={20} />
                <p className="text-sm font-semibold">{savedCount} items added to your Content Calendar</p>
                <a href="/calendar" className="ml-auto op-btn-secondary text-sm">Open Calendar →</a>
              </div>
            )}
          </div>
        </div>
      )}
    </PageContainer>
  );
};
export default CampaignBundle;
